import { RoleFeatureFlag } from '../types/roleFeature';
// Import the mock service last to ensure it's initialized after everything else
import mockRoleFeatureService from './mockRoleFeatureService';

const API_URL = process.env.REACT_APP_API_URL || '/api';
// Enable fallback to mock service after 2 failed attempts
const USE_MOCK_FALLBACK = true;

// Helper function to implement retries
const fetchWithRetry = async (
  url: string, 
  options: RequestInit, 
  retries = 2, 
  delay = 1000
): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    if (response.ok) return response;
    
    // If we got a 401 or 403, don't retry - it's an auth issue
    if (response.status === 401 || response.status === 403) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    // If we're out of retries, throw the error
    if (retries <= 0) throw new Error(`Error ${response.status}: ${response.statusText}`);
    
    // Otherwise, wait and retry
    console.log(`Retrying request to ${url} (${retries} attempts left)...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return fetchWithRetry(url, options, retries - 1, delay * 1.5);
  } catch (error) {
    if (retries <= 0) throw error;
    console.log(`Network error, retrying request to ${url} (${retries} attempts left)...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return fetchWithRetry(url, options, retries - 1, delay * 1.5);
  }
};

export const roleFeatureService = {
  // Get all feature flags
  getAllFeatureFlags: async (): Promise<RoleFeatureFlag[]> => {
    try {
      const response = await fetchWithRetry(`${API_URL}/role-features`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
      
      if (USE_MOCK_FALLBACK) {
        console.log('Falling back to mock feature flag service');
        return mockRoleFeatureService.getAllFeatureFlags();
      }
      
      throw error;
    }
  },
  
  // Get feature flags for a specific role
  getRoleFeatures: async (roleId: string): Promise<RoleFeatureFlag[]> => {
    try {
      console.log(`Fetching feature flags for role ${roleId}...`);
      
      const response = await fetchWithRetry(`${API_URL}/role-features/${roleId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch feature flags for role ${roleId}:`, error);
      
      if (USE_MOCK_FALLBACK) {
        console.log(`Falling back to mock feature flag service for role ${roleId}`);
        return mockRoleFeatureService.getRoleFeatures(roleId);
      }
      
      throw error;
    }
  },
  
  // Update a feature flag
  updateFeatureFlag: async (roleId: string, featureKey: string, enabled: boolean): Promise<RoleFeatureFlag> => {
    try {
      console.log(`Updating feature flag ${featureKey} for role ${roleId} to ${enabled} (${typeof enabled})`);
      
      // Ensure we're sending a clean boolean value
      const cleanEnabled = Boolean(enabled);
      console.log(`Cleaned boolean value: ${cleanEnabled} (${typeof cleanEnabled})`);
      
      const response = await fetchWithRetry(`${API_URL}/role-features/${roleId}/${featureKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors',
        body: JSON.stringify({ enabled: cleanEnabled })
      });
      
      const data = await response.json();
      console.log('Feature flag update response:', data);
      return data.data;
    } catch (error) {
      console.error(`Failed to update feature flag ${featureKey} for role ${roleId}:`, error);
      
      if (USE_MOCK_FALLBACK) {
        console.log(`Falling back to mock feature flag service for updating ${featureKey}`);
        return mockRoleFeatureService.updateFeatureFlag(roleId, featureKey, enabled);
      }
      
      throw error;
    }
  }
};

export default roleFeatureService; 